import React, { useState, useRef, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class RealtimeAudioChat {
    constructor() {
        this.peerConnection = null;
        this.dataChannel = null;
        this.audioElement = null;
        this.onStatusChange = null;
        this.onLog = null;
    }

    log(message, type = 'info') {
        if (this.onLog) {
            this.onLog({ message, type, time: new Date().toLocaleTimeString() });
        }
        console.log(`[${type.toUpperCase()}] ${message}`);
    }

    async init() {
        try {
            this.log('Getting session token from backend...', 'info');
            
            // Get session from backend
            const tokenResponse = await fetch(`${BACKEND_URL}/api/v1/realtime/session`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                }
            });
            
            if (!tokenResponse.ok) {
                throw new Error(`Failed to get session token: ${tokenResponse.status}`);
            }
            
            const data = await tokenResponse.json();
            if (!data.client_secret?.value) {
                throw new Error("No client_secret in response");
            }
            
            this.log('✓ Session token received', 'success');
            
            // Create WebRTC peer connection
            this.log('Creating WebRTC peer connection...', 'info');
            this.peerConnection = new RTCPeerConnection();
            
            // Setup audio element
            this.setupAudioElement();
            
            // Setup local audio (microphone)
            await this.setupLocalAudio();
            
            // Setup data channel
            this.setupDataChannel();
            
            // Create and send offer
            this.log('Creating WebRTC offer...', 'info');
            const offer = await this.peerConnection.createOffer();
            await this.peerConnection.setLocalDescription(offer);
            
            // Send offer to backend and get answer
            this.log('Negotiating with backend...', 'info');
            const response = await fetch(`${BACKEND_URL}/api/v1/realtime/negotiate`, {
                method: "POST",
                body: offer.sdp,
                headers: {
                    "Content-Type": "application/sdp"
                }
            });
            
            if (!response.ok) {
                throw new Error(`Negotiation failed: ${response.status}`);
            }
            
            const { sdp: answerSdp } = await response.json();
            const answer = {
                type: "answer",
                sdp: answerSdp
            };
            
            await this.peerConnection.setRemoteDescription(answer);
            
            this.log('✓ WebRTC connection established!', 'success');
            if (this.onStatusChange) {
                this.onStatusChange('connected');
            }
            
        } catch (error) {
            this.log(`✗ Error: ${error.message}`, 'error');
            if (this.onStatusChange) {
                this.onStatusChange('error');
            }
            throw error;
        }
    }

    setupAudioElement() {
        this.log('Setting up audio playback...', 'info');
        this.audioElement = document.createElement("audio");
        this.audioElement.autoplay = true;
        document.body.appendChild(this.audioElement);

        this.peerConnection.ontrack = (event) => {
            this.log('✓ Audio track received', 'success');
            this.audioElement.srcObject = event.streams[0];
        };
    }

    async setupLocalAudio() {
        try {
            this.log('Requesting microphone access...', 'info');
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.log('✓ Microphone access granted', 'success');
            
            stream.getTracks().forEach(track => {
                this.peerConnection.addTrack(track, stream);
            });
        } catch (error) {
            this.log(`✗ Microphone access denied: ${error.message}`, 'error');
            throw new Error('Microphone access is required for voice chat');
        }
    }

    setupDataChannel() {
        this.log('Setting up data channel...', 'info');
        this.dataChannel = this.peerConnection.createDataChannel("oai-events");
        
        this.dataChannel.onopen = () => {
            this.log('✓ Data channel opened', 'success');
        };
        
        this.dataChannel.onmessage = (event) => {
            this.log(`Event received: ${event.data.substring(0, 100)}...`, 'info');
        };
    }

    disconnect() {
        this.log('Disconnecting...', 'info');
        
        if (this.dataChannel) {
            this.dataChannel.close();
        }
        
        if (this.peerConnection) {
            this.peerConnection.close();
        }
        
        if (this.audioElement) {
            this.audioElement.remove();
        }
        
        if (this.onStatusChange) {
            this.onStatusChange('disconnected');
        }
        
        this.log('✓ Disconnected', 'success');
    }
}

const POCVoice = () => {
    const [status, setStatus] = useState('disconnected'); // disconnected, connecting, connected, error
    const [logs, setLogs] = useState([]);
    const chatRef = useRef(null);

    const addLog = (log) => {
        setLogs(prev => [...prev, log]);
    };

    const handleConnect = async () => {
        if (chatRef.current) {
            chatRef.current.disconnect();
        }

        setStatus('connecting');
        setLogs([]);

        const chat = new RealtimeAudioChat();
        chat.onStatusChange = setStatus;
        chat.onLog = addLog;
        chatRef.current = chat;

        try {
            await chat.init();
        } catch (error) {
            console.error('Connection error:', error);
        }
    };

    const handleDisconnect = () => {
        if (chatRef.current) {
            chatRef.current.disconnect();
            chatRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            if (chatRef.current) {
                chatRef.current.disconnect();
            }
        };
    }, []);

    const getStatusColor = () => {
        switch (status) {
            case 'connected': return '#10b981';
            case 'connecting': return '#f59e0b';
            case 'error': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const getStatusText = () => {
        switch (status) {
            case 'connected': return 'Connected - Speak now!';
            case 'connecting': return 'Connecting...';
            case 'error': return 'Connection Error';
            default: return 'Disconnected';
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            padding: '40px 20px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            <div style={{
                maxWidth: '800px',
                margin: '0 auto'
            }}>
                {/* Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '40px'
                }}>
                    <h1 style={{
                        fontSize: '3rem',
                        fontWeight: '700',
                        background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        marginBottom: '10px'
                    }}>
                        Lucy AI - Voice POC
                    </h1>
                    <p style={{
                        color: '#94a3b8',
                        fontSize: '1.1rem'
                    }}>
                        OpenAI Realtime API Test
                    </p>
                </div>

                {/* Status Card */}
                <div style={{
                    background: '#1e293b',
                    borderRadius: '16px',
                    padding: '30px',
                    marginBottom: '20px',
                    border: '1px solid #334155',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '20px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                background: getStatusColor(),
                                boxShadow: `0 0 20px ${getStatusColor()}`,
                                animation: status === 'connecting' ? 'pulse 2s infinite' : 'none'
                            }} />
                            <span style={{
                                color: '#e2e8f0',
                                fontSize: '1.2rem',
                                fontWeight: '600'
                            }}>
                                {getStatusText()}
                            </span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={handleConnect}
                                disabled={status === 'connecting' || status === 'connected'}
                                style={{
                                    padding: '12px 24px',
                                    background: status === 'connected' || status === 'connecting' 
                                        ? '#475569' 
                                        : 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: status === 'connected' || status === 'connecting' ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s'
                                }}
                                data-testid="connect-button"
                            >
                                Connect
                            </button>
                            
                            <button
                                onClick={handleDisconnect}
                                disabled={status !== 'connected'}
                                style={{
                                    padding: '12px 24px',
                                    background: status === 'connected' 
                                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                        : '#475569',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: status === 'connected' ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.3s'
                                }}
                                data-testid="disconnect-button"
                            >
                                Disconnect
                            </button>
                        </div>
                    </div>

                    {/* Instructions */}
                    <div style={{
                        background: '#0f172a',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #334155'
                    }}>
                        <h3 style={{
                            color: '#06b6d4',
                            fontSize: '1rem',
                            fontWeight: '600',
                            marginBottom: '12px'
                        }}>
                            Testing Instructions:
                        </h3>
                        <ol style={{
                            color: '#cbd5e1',
                            lineHeight: '1.8',
                            paddingLeft: '20px'
                        }}>
                            <li>Click "Connect" button</li>
                            <li>Allow microphone access when prompted</li>
                            <li>Wait for "Connected - Speak now!" status</li>
                            <li>Speak into your microphone (e.g., "Hello Lucy, how are you?")</li>
                            <li>Listen for Lucy's voice response</li>
                            <li>Click "Disconnect" when done</li>
                        </ol>
                    </div>
                </div>

                {/* Logs */}
                <div style={{
                    background: '#1e293b',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '1px solid #334155',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <h3 style={{
                        color: '#e2e8f0',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        marginBottom: '15px'
                    }}>
                        Connection Logs:
                    </h3>
                    <div style={{
                        background: '#0f172a',
                        borderRadius: '8px',
                        padding: '15px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                    }}>
                        {logs.length === 0 ? (
                            <div style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>
                                No logs yet. Click Connect to start.
                            </div>
                        ) : (
                            logs.map((log, index) => (
                                <div
                                    key={index}
                                    style={{
                                        color: log.type === 'error' ? '#ef4444' 
                                            : log.type === 'success' ? '#10b981'
                                            : '#94a3b8',
                                        marginBottom: '8px',
                                        paddingLeft: '10px',
                                        borderLeft: `3px solid ${
                                            log.type === 'error' ? '#ef4444'
                                            : log.type === 'success' ? '#10b981'
                                            : '#475569'
                                        }`
                                    }}
                                >
                                    <span style={{ color: '#64748b' }}>[{log.time}]</span> {log.message}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                }
            `}</style>
        </div>
    );
};

export default POCVoice;
